import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import ComplianceBanner from "@/components/ComplianceBanner";
import { ArrowRight, Certificate, ShieldCheck, Snowflake, Minus, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const primaryCtaRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    if (!primaryCtaRef.current) return;
    const el = primaryCtaRef.current;
    const io = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { rootMargin: "-40px 0px 0px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product]);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants?.[0] || null);
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) {
    return <div className="mx-auto max-w-[1400px] px-6 py-24 overline animate-pulse">Loading product…</div>;
  }
  if (product === false) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-24">
        <div className="overline mb-3">404</div>
        <h1 className="font-display text-4xl font-bold">Product not found</h1>
        <Link to="/shop" className="btn-outline mt-6 inline-flex">Back to catalog</Link>
      </div>
    );
  }

  const handleAdd = () => {
    add(product, variant, qty);
    toast.success(`${product.name} added to cart`);
  };

  const buyNow = () => {
    add(product, variant, qty);
    navigate("/cart");
  };

  return (
    <div className="page-enter">
      <ComplianceBanner />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-10 pb-24 grid lg:grid-cols-12 gap-10">
        {/* Image */}
        <div className="lg:col-span-6">
          <div className="product-image-frame aspect-[4/5] bg-white border border-white/10 overflow-hidden sticky top-24">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid-bg" />
            )}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-6">
          <div className="overline mb-3">
            <Link to={`/shop?category=${product.category_slug}`} className="link-underline">
              {product.category_slug.replace(/-/g, " ")}
            </Link>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight" data-testid="product-name">
            {product.name}
          </h1>
          {product.subtitle && <div className="text-lg text-zinc-500 mt-2">{product.subtitle}</div>}

          {product.short_description && (
            <p className="mt-6 text-zinc-700 leading-relaxed max-w-xl">{product.short_description}</p>
          )}

          <div className="mt-8 border-y border-black/10 py-6">
            <div className="overline mb-3">Configuration</div>
            {product.variants?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.sku}
                    onClick={() => setVariant(v)}
                    className={`px-4 py-2 border text-sm ${
                      variant?.sku === v.sku ? "bg-black text-white border-black" : "border-black/20 hover:border-black"
                    }`}
                    data-testid={`variant-${v.sku}`}
                  >
                    <span className="font-mono">{v.label}</span>
                    <span className="ml-3 opacity-70">{money(v.price_cents)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-zinc-500">Default configuration</div>
            )}
          </div>

          <div className="mt-8 flex items-end justify-between">
            <div>
              <div className="overline">Unit price</div>
              <div className="font-display text-4xl font-bold mt-1" data-testid="product-price">
                {money(variant?.price_cents ?? product.price_cents)}
              </div>
            </div>
            <div className="flex items-center border border-black/20">
              <button className="px-3 py-2" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec">-</button>
              <input
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                className="w-12 text-center font-mono outline-none"
                data-testid="qty-input"
              />
              <button className="px-3 py-2" onClick={() => setQty((q) => Math.min(99, q + 1))} data-testid="qty-inc">+</button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3" ref={primaryCtaRef}>
            <button className="btn-prm" onClick={handleAdd} data-testid="add-to-cart">
              Add to cart <ArrowRight size={16} />
            </button>
            <button className="btn-outline" onClick={buyNow} data-testid="buy-now">
              Buy now
            </button>
          </div>

          {/* Spec sheet */}
          <div className="mt-12">
            <div className="overline mb-3">Specification</div>
            <dl>
              {[
                ["CAS number", product.cas_number],
                ["Molecular formula", product.molecular_formula],
                ["Molecular weight", product.molecular_weight],
                ["Purity", product.purity],
                ["Storage", product.storage],
                ["Latest batch", product.latest_batch?.batch_number],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="data-row">
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
            </dl>
          </div>

          {product.description && (
            <div className="mt-10">
              <div className="overline mb-3">Description</div>
              <p className="text-zinc-700 leading-relaxed">{product.description}</p>
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FeatureBadge icon={<Certificate size={18} />} title="Batch-traceable" desc="Every unit tied to a lot." />
            <FeatureBadge icon={<ShieldCheck size={18} />} title="Independently tested" desc="Third-party spec check." />
            <FeatureBadge icon={<Snowflake size={18} />} title="Insulated packaging" desc="Safe transit, discreet." />
          </div>

          {product.coas?.length > 0 && (
            <div className="mt-8">
              <div className="overline mb-3">Certificates of analysis</div>
              <div className="space-y-2">
                {product.coas.map((coa) => (
                  <a
                    key={coa.id}
                    href={coa.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="card-lift flex items-center justify-between p-4 rounded-xl"
                    data-testid={`product-coa-${coa.batch_number}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display-plain font-semibold truncate">{coa.title}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-1 truncate">
                        Batch {coa.batch_number}
                        {coa.lab_name ? ` · ${coa.lab_name}` : ""}
                        {coa.tested_at ? ` · Tested ${coa.tested_at}` : ""}
                      </div>
                    </div>
                    <span className="chip chip-accent shrink-0 ml-3">View →</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <p className="mt-10 text-xs font-mono uppercase tracking-widest text-zinc-500">
            Intended for laboratory research only. Not for human or veterinary use.
          </p>
        </div>
      </div>

      {/* Sticky Buy Bar */}
      <div
        aria-hidden={!showStickyBar}
        className={`fixed inset-x-0 bottom-0 z-40 transition-all duration-300 ease-out ${
          showStickyBar ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        data-testid="sticky-buy-bar"
      >
        <div className="mx-auto max-w-[1400px] px-3 pb-3 md:pb-4">
          <div className="nav-blur border border-white/10 rounded-2xl px-3 py-2.5 md:px-5 md:py-3 flex items-center gap-3 md:gap-5 shadow-2xl">
            {/* Thumb */}
            <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 rounded-xl overflow-hidden bg-black/30 border border-white/10">
              {product.image_url && (
                <img src={product.image_url} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            {/* Info */}
            <div className="min-w-0 flex-1">
              <div className="font-display-plain text-sm md:text-base font-semibold truncate">
                {product.name}
              </div>
              <div className="text-xs font-mono text-zinc-500 truncate">
                {variant?.label ? `${variant.label} · ` : ""}
                <span className="text-white">{money(variant?.price_cents ?? product.price_cents)}</span>
              </div>
            </div>
            {/* Qty */}
            <div className="hidden sm:flex items-center border border-white/15 rounded-full">
              <button
                className="px-2.5 py-1.5 hover:text-blue-400 transition-colors"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                aria-label="Decrease"
                data-testid="sticky-qty-dec"
              >
                <Minus size={14} weight="bold" />
              </button>
              <span className="w-8 text-center font-mono text-sm" data-testid="sticky-qty">{qty}</span>
              <button
                className="px-2.5 py-1.5 hover:text-blue-400 transition-colors"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                aria-label="Increase"
                data-testid="sticky-qty-inc"
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
            {/* CTA */}
            <button
              className="btn-prm !py-2.5 !px-3 md:!px-5 !text-[11px] md:!text-[13px] shrink-0"
              onClick={handleAdd}
              data-testid="sticky-add-to-cart"
            >
              Add
              <ArrowRight size={14} weight="bold" className="hidden md:inline" />
            </button>
            <button
              className="inline-flex items-center justify-center px-3 md:px-4 py-2.5 rounded-full bg-white text-black hover:bg-blue-500 hover:text-white transition-colors font-semibold text-[11px] md:text-[13px] shrink-0"
              onClick={buyNow}
              data-testid="sticky-buy-now"
            >
              Buy now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureBadge({ icon, title, desc }) {
  return (
    <div className="border border-black/10 p-4">
      <div className="mb-2">{icon}</div>
      <div className="font-display font-semibold text-sm">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
    </div>
  );
}
